import { useState, useContext, useEffect } from 'react';
import { Box, Button, Dialog, DialogActions, DialogContent, Grid, InputAdornment, Typography } from '@mui/material';
import { Formik, Form, Field } from 'formik';
import { TextField } from 'formik-mui';
import * as Yup from 'yup';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
// Our Components
import { BusinessContext } from 'context/businesses/businessContext';
import { BUSINESSES_ACTIONS } from 'context/businesses/businessReducer';
import ConfirmationDialog from './ConfirmationDialog';
import SuccessAlert from '../SuccessAlert';
// Services
import { Businesses } from 'lib/api';
// Utils and Helpers
import { hasCreditLine } from 'helpers/businesses';
import { numberFormat } from 'helpers/i18n';
// Assets
import './ApproveBusinessModal.css';

const { SHOW_APPROVAL_MODAL, HIDE_APPROVAL_MODAL, RELOAD_BUSINESSES } = BUSINESSES_ACTIONS;
const MAX_SPEND_LIMIT = 15_000;
const INITIAL_VALUES = {
    spend_limit: '',
    satoshi_amount: '',
    reward_rate: ''
};

const ApproveBusinessModal = () => {
    const {
        state: { isApproveModalOpen, selectedBusiness },
        dispatch
    } = useContext(BusinessContext);
    const [isLoading, setIsLoading] = useState(false);
    const [isApproved, setIsApproved] = useState(false);
    const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [formValues, setFormValues] = useState({ ...INITIAL_VALUES });

    const APPROVAL_SCHEMA = Yup.object().shape({
        ...(hasCreditLine(selectedBusiness) && { spend_limit: Yup.number().min(0, 'Invalid amount').required('Required') }),
        satoshi_amount: Yup.number().integer('Invalid amount').min(0, 'Invalid amount').required('Required'),
        reward_rate: Yup.number().positive('Invalid amount').required('Required')
    });

    useEffect(() => {
        if (!isApproveModalOpen) {
            setErrorMessage('');
        }
    }, [isApproveModalOpen]);

    const handleCancelClick = () => {
        dispatch({ type: HIDE_APPROVAL_MODAL });
        setFormValues({ ...INITIAL_VALUES });
    };

    const handleGoBackClick = () => {
        setIsConfirmDialogOpen(false);
        dispatch({ type: SHOW_APPROVAL_MODAL });
    };

    const handleApproveApplicant = async (values, actions) => {
        setFormValues(values);
        setErrorMessage('');
        if (values.spend_limit >= MAX_SPEND_LIMIT) {
            dispatch({ type: HIDE_APPROVAL_MODAL });
            setIsConfirmDialogOpen(true);
            actions.setSubmitting(false);
            return;
        }

        try {
            const rewardRate = Number(values.reward_rate) / 100;
            const body = {
                ...(hasCreditLine(selectedBusiness) && { creditLimit: values.spend_limit }),
                cardCreation: true,
                satoshi: values.satoshi_amount,
                rewardRate
            };
            await Businesses.approve(selectedBusiness.id_business, body);

            dispatch({ type: HIDE_APPROVAL_MODAL });
            setIsApproved(true);
            setFormValues({ ...INITIAL_VALUES });
            dispatch({ type: RELOAD_BUSINESSES });
        } catch (error) {
            actions.setSubmitting(false);
            setErrorMessage(error?.response?.data?.message || 'An unexpected error ocurred. Please try again.');
        }
    };

    const handleConfirmClick = async () => {
        setIsLoading(true);

        try {
            const rewardRate = Number(formValues.reward_rate) / 100;
            const body = {
                ...(hasCreditLine(selectedBusiness) && { creditLimit: formValues.spend_limit }),
                cardCreation: true,
                satoshi: formValues.satoshi_amount,
                rewardRate
            };
            await Businesses.approve(selectedBusiness.id_business, body);

            setIsConfirmDialogOpen(false);
            setIsApproved(true);
            setFormValues({ ...INITIAL_VALUES });
            dispatch({ type: RELOAD_BUSINESSES });
            setIsLoading(false);
        } catch (error) {
            setIsLoading(false);
            setErrorMessage(error?.response?.data?.message || 'An unexpected error ocurred. Please try again.');
        }
    };

    const handleApprovedButton = () => {
        setIsApproved(false);
    };

    return (
        <>
            <Dialog open={isApproveModalOpen} onClose={() => {}} maxWidth="xs">
                <DialogContent sx={{ padding: '32px', width: '384px' }}>
                    <Box sx={{ marginBottom: '32px' }}>
                        <Box>
                            <Typography className="data-label">Business ID:</Typography>
                            <Typography className="text-secondary">{selectedBusiness?.id_business}</Typography>
                        </Box>
                        <Box>
                            <Typography className="data-label">Business name:</Typography>
                            <Typography className="text-secondary">{selectedBusiness?.legal_name}</Typography>
                        </Box>
                        <Box>
                            <Typography className="data-label">Admin name:</Typography>
                            <Typography className="text-secondary">
                                {selectedBusiness?.first_name} {selectedBusiness?.last_name}
                            </Typography>
                        </Box>
                        <Box>
                            <Typography className="data-label">Current spend limit:</Typography>
                            <Typography className="text-secondary">
                                {numberFormat(selectedBusiness?.credit_limit || 0)}
                            </Typography>
                        </Box>
                    </Box>
                    <Formik initialValues={formValues} validationSchema={APPROVAL_SCHEMA} onSubmit={handleApproveApplicant}>
                        {({ isSubmitting, isValid, dirty, values }) => (
                            <Form>
                                <Box sx={{ paddingBottom: '10px' }}>
                                    <Box>
                                        <Typography className="form-title">Approving Request</Typography>
                                    </Box>
                                    {hasCreditLine(selectedBusiness) && (
                                        <>
                                            <Box>
                                                <Typography className="text-secondary">Assign account spend limit</Typography>
                                            </Box>

                                            <Field
                                                type="text"
                                                name="spend_limit"
                                                component={TextField}
                                                InputProps={{
                                                    startAdornment: (
                                                        <InputAdornment position="start">
                                                            <AttachMoneyIcon fontSize="small" />
                                                        </InputAdornment>
                                                    )
                                                }}
                                                placeholder="Spend Limit"
                                                margin="dense"
                                                fullWidth
                                                size="small"
                                            />
                                        </>
                                    )}
                                </Box>

                                <Typography className="text-secondary">Assign corresponding Satoshi amount</Typography>
                                <Grid container>
                                    <Grid item xs={6}>
                                        <Field
                                            type="text"
                                            name="satoshi_amount"
                                            component={TextField}
                                            sx={{ width: '162px' }}
                                            placeholder="Satoshi Amount"
                                            margin="dense"
                                            size="small"
                                        />
                                    </Grid>
                                    <Grid item xs={6}>
                                        <Field
                                            type="text"
                                            name="reward_rate"
                                            component={TextField}
                                            sx={{ width: '148px', marginLeft: '15px' }}
                                            placeholder="Reward Rate"
                                            margin="dense"
                                            size="small"
                                            InputProps={{
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        <Typography sx={{ fontWeight: 'bold' }}>%</Typography>
                                                    </InputAdornment>
                                                )
                                            }}
                                        />
                                    </Grid>
                                </Grid>
                                {errorMessage && (
                                    <Typography className="text-error">
                                        There was an error trying to approve the applicant.
                                        {errorMessage}
                                    </Typography>
                                )}
                                <DialogActions sx={{ marginTop: '32px', padding: '0px' }}>
                                    <Button
                                        variant="outlined"
                                        sx={{ color: '#7D95A5', borderColor: '#7D95A5' }}
                                        onClick={handleCancelClick}
                                        disabled={isSubmitting}>
                                        Cancel
                                    </Button>

                                    <Button
                                        type="submit"
                                        variant="contained"
                                        disabled={(!dirty && values.spend_limit === '') || !isValid || isSubmitting}>
                                        Confirm
                                    </Button>
                                </DialogActions>
                            </Form>
                        )}
                    </Formik>
                </DialogContent>
            </Dialog>

            <ConfirmationDialog
                open={isConfirmDialogOpen}
                currentValues={formValues}
                errorMessage={errorMessage}
                isLoading={isLoading}
                onConfirmClick={handleConfirmClick}
                onGoBackClick={handleGoBackClick}
            />

            <SuccessAlert
                open={isApproved}
                title="Request Successfully Approved"
                description="Business will receive a confirmation email"
                onAlertClose={handleApprovedButton}
            />
        </>
    );
};

export default ApproveBusinessModal;
